import React, { FormEvent, useEffect, useState } from 'react'
import { AgencyNote } from '../../models'
import { createAgencyNote, deleteAgencyNote, updateAgencyNote, toastSuccess } from '../../api'
import { getAuthUser } from '../../helpers'
import { NoteDiv, TooltipWrapper } from '../common'
import AutoResizeTextarea from '../common/AutoResizeTextarea'
import CollapsibleNote from '../common/CollapsibleNote'

import defineAbilityFor from './defineAbility'

interface NoteProps {
    agencyId: number
    agencyNotes: AgencyNote[]
}

export const AgencyNotes = (props: NoteProps) => {
    const { agencyId, agencyNotes } = props

    const [isAdding, setIsAdding] = useState(false)
    const [notes, setNotes] = useState(agencyNotes)
    const [note, setNote] = useState('')
    const [isImportant, setIsImportant] = useState(false)

    useEffect(() => {
        setNotes(agencyNotes)
    }, [agencyNotes])

    const handleDelete = (agencyNoteId: number) => {
        deleteAgencyNote(agencyNoteId)
            .then((json) => {
                setNotes(notes.filter((note) => note.id !== agencyNoteId))

                toastSuccess('Note Deleted')
            })
            .catch(() => {})
    }

    const handleUpdate = (agencyNoteId: number) => {
        const note = notes.find((n) => n.id === agencyNoteId)
        if (!note) return

        const newImportant = !note.important

        setNotes((prev) =>
            prev.map((n) => (n.id === agencyNoteId ? { ...n, important: newImportant } : n)),
        )

        updateAgencyNote({ agencyNoteId, important: newImportant })
            .then((json) => {
                toastSuccess('Note updated!')
                setNotes((prev) =>
                    prev.map((n) => (n.id === agencyNoteId ? { ...n, ...json.data } : n)),
                )
            })
            .catch(() => {
                setNotes((prev) =>
                    prev.map((n) => (n.id === agencyNoteId ? { ...n, important: note.important } : n)),
                )
            })
    }

    const handleCancel = () => {
        setNote('')
        setIsAdding(false)
    }

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault()

        const params = {
            agencyId,
            note,
            important: isImportant,
        }

        createAgencyNote(params)
            .then((json) => {
                setNotes([...notes, new AgencyNote(json.data)])
                setNote('')
                setIsAdding(false)
                setIsImportant(false)

                toastSuccess('Note Created')
            })
            .catch(() => {})
    }

    const ability = defineAbilityFor(getAuthUser())

    const importantNotes = notes.filter((note) => note.important)

    return (
        <>
            <div className="d-flex justify-content-between mb-3">
                <h3>Notes</h3>
            </div>
            <form className="mb-3" onSubmit={(e) => handleSubmit(e)}>
                <AutoResizeTextarea
                    className="form-control mb-3"
                    onChange={setNote}
                    onFocus={() => setIsAdding(true)}
                    placeholder="Add a note"
                    value={note}
                    disabled={false}
                />
                {isAdding && (
                    <>
                        <button className="btn btn-primary me-2" type="submit">
                            Submit
                        </button>
                        <button className="btn btn-outline-secondary" onClick={handleCancel}>
                            Cancel
                        </button>
                    </>
                )}
            </form>
            {importantNotes.length > 0 && (
                <>
                    <h4 className="border-bottom">Favorite Notes</h4>
                    {importantNotes.map((note) => (
                        <div
                            className="card mb-3 border border-danger"
                            key={'important_' + note.id}
                        >
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <div>
                                    <strong className="me-4">{note.createdBy}</strong>
                                    {note.created_at_local.format('MMM D, YYYY [at] h:mm A')}
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <TooltipWrapper
                                        condition={!ability.can('Update', 'AgencyNote')}
                                        element={
                                            <span
                                                onClick={() => handleUpdate(note.id)}
                                                style={{
                                                    fontSize: '2rem',
                                                    cursor: 'pointer',
                                                    color: note.important ? '#ffc107' : '#ccc',
                                                    transition:
                                                        'transform 0.2s ease, color 0.2s ease',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.color = '#ffc107'
                                                    e.currentTarget.style.transform =
                                                        'scale(1.2)'
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.color =
                                                        note.important ? '#ffc107' : '#ccc'
                                                    e.currentTarget.style.transform =
                                                        'scale(1)'
                                                }}
                                            >
                                                {note.important ? '★' : '☆'}
                                            </span>
                                        }
                                        message={'Executive permissions required'}
                                    />
                                    <TooltipWrapper
                                        condition={!ability.can('Delete', 'AgencyNote')}
                                        element={
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                disabled={!ability.can('Delete', 'AgencyNote')}
                                                onClick={() => {
                                                    if (
                                                        window.confirm(
                                                            'Are you sure you want to delete this note?',
                                                        )
                                                    ) {
                                                        handleDelete(note.id)
                                                    }
                                                }}
                                            >
                                                Delete
                                            </button>
                                        }
                                        message={'Executive permissions required'}
                                    />
                                </div>
                            </div>
                            <NoteDiv className="card-body">
                                    <CollapsibleNote text={note.note} maxLength={500} />
                            </NoteDiv>
                        </div>
                    ))}
                </>
            )}

            {notes.length > 0 && (
                <>
                    <h4 className="border-bottom">All Notes</h4>
                    {[...notes]
                        .sort((a, b) => {
                            if (b.created_at.isAfter(a.created_at)) {
                                return 1
                            }

                            if (a.created_at.isAfter(b.created_at)) {
                                return -1
                            }

                            return 0
                        })
                        .map((note) => (
                            <div
                                className={`card mb-3 ${
                                    note.important ? 'border border-danger' : ''
                                }`}
                                key={`note_card_${note.id}`}
                            >
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong className="me-4">{note.createdBy}</strong>
                                        {note.created_at_local.format(
                                            'MMM D, YYYY [at] h:mm A',
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <TooltipWrapper
                                            condition={!ability.can('Update', 'AgencyNote')}
                                            element={
                                                <span
                                                    onClick={() => handleUpdate(note.id)}
                                                    style={{
                                                        fontSize: '2rem',
                                                        cursor: 'pointer',
                                                        color: note.important
                                                            ? '#ffc107'
                                                            : '#ccc',
                                                        transition:
                                                            'transform 0.2s ease, color 0.2s ease',
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.color = '#ffc107'
                                                        e.currentTarget.style.transform =
                                                            'scale(1.2)'
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.color =
                                                            note.important ? '#ffc107' : '#ccc'
                                                        e.currentTarget.style.transform =
                                                            'scale(1)'
                                                    }}
                                                >
                                                    {note.important ? '★' : '☆'}
                                                </span>
                                            }
                                            message={'Executive permissions required'}
                                        />
                                        <TooltipWrapper
                                            condition={!ability.can('Delete', 'AgencyNote')}
                                            element={
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    disabled={
                                                        !ability.can('Delete', 'AgencyNote')
                                                    }
                                                    onClick={() => {
                                                        if (
                                                            window.confirm(
                                                                'Are you sure you want to delete this note?',
                                                            )
                                                        ) {
                                                            handleDelete(note.id)
                                                        }
                                                    }}
                                                >
                                                    Delete
                                                </button>
                                            }
                                            message={'Executive permissions required'}
                                        />
                                    </div>
                                </div>
                                <NoteDiv className="card-body">
                                    <CollapsibleNote text={note.note} maxLength={500} />
                                </NoteDiv>
                            </div>
                        ))}
                </>
            )}
        </>
    )
}
